/**
 * Custom React hook and utility for throttled analytics event dispatching.
 *
 * This module provides a singleton event queue and a React hook to ensure analytics events
 * are dispatched at a controlled rate (at most one per second), preventing overload of the analytics service.
 * Use `pushAnalyticsEvent` to queue events, and call `useAnalytics` once in your app to enable dispatching.
 */

import { trackEvent } from 'fathom-client';
import { useInterval } from '../../hooks';

export const MIN_SEND_INTERVAL = 1000; // Minimum interval between sending events in milliseconds
export const NOMINAL_SEND_INTERVAL = 1100; // Nominal interval to aim for when dispatching events, slightly above the minimum to provide a buffer
export const MAX_QUEUE_LENGTH = 100; // Maximum number of events to queue to prevent memory issues

/**
 * Singleton class that manages a queue of analytics events and dispatches them at a controlled rate.
 * Prevents memory issues by enforcing a maximum queue length.
 * @private, only exported for testing purposes.
 */
export class AnalyticsEventQueue {
  /**
   * Singleton instance of AnalyticsEventQueue.
   */
  static instance: AnalyticsEventQueue | null = null;

  /**
   * Internal queue holding analytics event strings.
   */
  private analyticsEventQueue: string[];

  /**
   * Timestamp of the last dispatched event, used to enforce throttling.
   * Not strictly necessary with useInterval, but just in case we have multiple invocations of the hook.
   */
  private lastDispatchTime: number = 0;

  /**
   * Returns the singleton instance of AnalyticsEventQueue.
   * @returns {AnalyticsEventQueue}
   */
  static getInstance() {
    if (!AnalyticsEventQueue.instance) {
      AnalyticsEventQueue.instance = new AnalyticsEventQueue();
    }
    return AnalyticsEventQueue.instance;
  }

  /**
   * Private constructor to enforce singleton pattern. Use getInstance() instead.
   */
  private constructor() {
    this.analyticsEventQueue = [];
    this.lastDispatchTime = 0;
  }

  /**
   * Adds an analytics event to the queue. If the queue is full, removes the oldest event.
   * @param {string} event - The analytics event string to queue.
   */
  public pushAnalyticsEvent(event: string) {
    if (this.analyticsEventQueue.length >= MAX_QUEUE_LENGTH) {
      this.analyticsEventQueue.shift();
    }
    this.analyticsEventQueue.push(event);
  }

  /**
   * Removes the next event from the queue and dispatches it using the analytics service.
   * If the queue is empty, nothing is dispatched.
   */
  public popAndDispatchEvent() {
    const timeSinceLastDispatch = Date.now() - this.lastDispatchTime;
    if (timeSinceLastDispatch < MIN_SEND_INTERVAL) {
      // Not enough time has passed since the last dispatch, so we skip this cycle to enforce throttling.
      return;
    }
    const nextEvent = this.analyticsEventQueue.shift();
    if (nextEvent) {
      trackEvent(nextEvent);
      this.lastDispatchTime = Date.now();
    }
  }
}

/**
 * Queues an analytics event for throttled dispatching.
 *
 * Call this function anywhere in your app to log an analytics event. The event will be dispatched
 * at most once per second by the `useAnalytics` hook.
 *
 * @param {string} event - The analytics event string to log.
 */
export const pushAnalyticsEvent = (event: string) => {
  const analyticsEventQueue = AnalyticsEventQueue.getInstance();
  analyticsEventQueue.pushAnalyticsEvent(event);
};

/**
 * React hook that manages throttled dispatching of analytics events.
 *
 * Call this hook once in your app (e.g., in the root component) to enable
 * throttled analytics event dispatching for the entire app. It will dispatch
 * queued events at most once per second.
 */
const useAnalytics = () => {
  const analyticsEventQueue = AnalyticsEventQueue.getInstance();

  useInterval(
    () => {
      analyticsEventQueue.popAndDispatchEvent();
    },
    NOMINAL_SEND_INTERVAL,
    true
  );
};

export default useAnalytics;
